(() => {
  const MONEY_FORMAT = window.themeVariables?.settings?.moneyFormat || '${{amount}}';

  function formatMoney(cents, format = MONEY_FORMAT) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = (Number(cents) || 0) / 100;
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return '0';
      const parts = number.toFixed(precision).split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return precision > 0 ? `${dollars}${decimal}${parts[1]}` : dollars;
    }

    const match = format.match(placeholderRegex);
    if (!match) return formatWithDelimiters(value, 2);

    let formatted;
    switch (match[1]) {
      case 'amount':
        formatted = formatWithDelimiters(value, 2);
        break;
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      default:
        formatted = formatWithDelimiters(value, 2);
    }

    return format.replace(placeholderRegex, formatted);
  }

  function createConfigId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `cfg-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  class KeyboardConfigurator extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-configurator-json]');
      if (!dataEl) return;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.error('[keyboard-configurator] invalid config JSON', error);
        return;
      }

      this.primarySelected = {};
      this.addonSelected = {};
      this.imageCache = new Map();
      this.layerPending = {};
      this.priceRoot = this.querySelector('[data-config-price]');
      this.salePriceEl = this.querySelector('[data-config-sale-price]');
      this.priceEl = this.querySelector('[data-config-total-price]');
      this.compareAtEl = this.querySelector('[data-config-compare-at]');
      this.comparePriceEl = this.querySelector('[data-config-compare-price]');
      this.saveBadgeEl = this.querySelector('[data-config-save-badge]');
      this.errorEl = this.querySelector('[data-config-error]');
      this.atcButton = this.querySelector('[data-config-atc]');
      this.stageEl = this.querySelector('[data-config-stage]');
      this.saveTemplate = this.priceRoot?.getAttribute('data-save-template') || 'Save __AMOUNT__';
      this.#markPriceElementsIgnoredByTpo();

      this.#initPrimaryDefaults();
      this.#initAddonDefaults();
      this.#seedImageCacheFromDom();
      this.#preloadLayerImages({ primaryFirst: true });
      this.#bindEvents();
      this.#render();
    }

    #normalizeUrl(url) {
      if (!url) return '';
      try {
        return new URL(url, window.location.href).href;
      } catch (_) {
        return String(url);
      }
    }

    #urlsMatch(a, b) {
      const left = this.#normalizeUrl(a);
      const right = this.#normalizeUrl(b);
      return Boolean(left) && left === right;
    }

    #getPrimary() {
      return this.data.primary || null;
    }

    #getAddonLayers() {
      return this.data.addonLayers || [];
    }

    #initPrimaryDefaults() {
      const primary = this.#getPrimary();
      if (!primary?.variants?.length) return;

      const fromInitialId = primary.initialVariantId
        ? primary.variants.find(
            (variant) => variant.variantId === primary.initialVariantId && variant.available
          )
        : null;

      const preferred =
        fromInitialId ||
        primary.variants.find((variant) => variant.title === primary.defaultOption && variant.available) ||
        primary.variants.find((variant) => variant.available) ||
        primary.variants[0];

      if (!preferred) return;

      preferred.options.forEach((value, index) => {
        this.primarySelected[index + 1] = value;
      });
    }

    #initAddonDefaults() {
      this.#getAddonLayers().forEach((layer) => {
        const preferred =
          layer.options.find((option) => option.title === layer.defaultOption && option.available) ||
          layer.options.find((option) => option.available) ||
          layer.options[0];

        if (preferred) this.addonSelected[layer.id] = preferred.variantId;
      });
    }

    #getPrimaryVariant() {
      const primary = this.#getPrimary();
      if (!primary?.variants?.length) return null;

      return (
        primary.variants.find((variant) =>
          variant.options.every((value, index) => this.primarySelected[index + 1] === value)
        ) || null
      );
    }

    #applyPrimaryOptionValue(position, value) {
      this.primarySelected[position] = value;

      const variants = this.#getPrimary()?.variants || [];
      let match = variants.find((variant) => {
        if (!variant.available) return false;
        for (let p = 1; p <= position; p += 1) {
          if (variant.options[p - 1] !== this.primarySelected[p]) return false;
        }
        return true;
      });

      if (!match) {
        match = variants.find((variant) => variant.available && variant.options[position - 1] === value);
      }

      if (match) {
        match.options.forEach((optionValue, index) => {
          this.primarySelected[index + 1] = optionValue;
        });
      }
    }

    #isPrimaryOptionAvailable(position, value) {
      const variants = this.#getPrimary()?.variants || [];

      return variants.some((variant) => {
        if (!variant.available || variant.options[position - 1] !== value) return false;

        for (let p = 1; p < position; p += 1) {
          if (variant.options[p - 1] !== this.primarySelected[p]) return false;
        }

        return true;
      });
    }

    #seedImageCacheFromDom() {
      this.stageEl?.querySelectorAll('.kb-config__layer[src]').forEach((img) => {
        const src = img.getAttribute('src');
        if (!src) return;
        const key = this.#normalizeUrl(src);
        if (img.complete && img.naturalWidth > 0) {
          this.imageCache.set(key, Promise.resolve());
        } else {
          this.#preloadImage(src);
        }
      });
    }

    #preloadImage(url) {
      if (!url) return Promise.resolve();
      const key = this.#normalizeUrl(url);
      if (this.imageCache.has(key)) return this.imageCache.get(key);

      const promise = new Promise((resolve) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = async () => {
          try {
            if (img.decode) await img.decode();
          } catch (_) {
            /* ignore decode errors */
          }
          resolve();
        };
        img.onerror = () => resolve();
        img.src = url;
      });

      this.imageCache.set(key, promise);
      return promise;
    }

    #waitForPaint() {
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });
    }

    #readyImageElement(img, url) {
      const key = this.#normalizeUrl(url);

      return new Promise((resolve) => {
        const finish = async () => {
          try {
            if (img.decode) await img.decode();
          } catch (_) {
            /* ignore */
          }
          this.imageCache.set(key, Promise.resolve());
          resolve();
        };

        if (this.#urlsMatch(img.getAttribute('src'), url) && img.complete && img.naturalWidth > 0) {
          finish();
          return;
        }

        const onLoad = () => {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onError);
          finish();
        };
        const onError = () => {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onError);
          resolve();
        };

        img.addEventListener('load', onLoad);
        img.addEventListener('error', onError);
        img.src = url;

        if (img.complete && img.naturalWidth > 0) {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onError);
          finish();
        }
      });
    }

    #preloadLayerImages({ primaryFirst = false } = {}) {
      const primary = this.#getPrimary();
      const addonLayers = this.#getAddonLayers();
      const ordered = primaryFirst && primary ? [primary, ...addonLayers] : addonLayers;

      if (!primaryFirst) {
        const selectedPrimary = this.#getPrimaryVariant();
        if (primary?.affectsPreview && selectedPrimary?.image) this.#preloadImage(selectedPrimary.image);
        (primary?.variants || []).forEach((variant) => {
          if (variant.image && variant.image !== selectedPrimary?.image) this.#preloadImage(variant.image);
        });
      }

      ordered.forEach((layer) => {
        if (layer === primary) {
          if (!layer.affectsPreview) return;
          const selected = this.#getPrimaryVariant();
          if (selected?.image) this.#preloadImage(selected.image);
          (layer.variants || []).forEach((variant) => {
            if (variant.image && variant.image !== selected?.image) this.#preloadImage(variant.image);
          });
          return;
        }

        if (!layer.affectsPreview) return;
        const selected = this.#getSelectedAddonOption(layer);
        if (selected?.image) this.#preloadImage(selected.image);
        (layer.options || []).forEach((option) => {
          if (option.image && option.image !== selected?.image) this.#preloadImage(option.image);
        });
      });
    }

    #bindEvents() {
      this.addEventListener('click', (event) => {
        const primaryButton = event.target.closest('[data-config-primary-option]');
        if (primaryButton && !primaryButton.disabled) {
          const position = Number(primaryButton.getAttribute('data-option-position'));
          const value = primaryButton.getAttribute('data-option-value');
          this.#applyPrimaryOptionValue(position, value);
          this.#render();
          return;
        }

        const addonButton = event.target.closest('[data-config-option]');
        if (addonButton && !addonButton.disabled) {
          const layerId = addonButton.getAttribute('data-layer-id');
          const variantId = Number(addonButton.getAttribute('data-variant-id'));
          this.addonSelected[layerId] = variantId;
          this.#render();
          return;
        }

        if (event.target.closest('[data-config-atc]')) {
          event.preventDefault();
          this.#addToCart();
        }
      });
    }

    #getSelectedAddonOption(layer) {
      const variantId = this.addonSelected[layer.id];
      return layer.options.find((option) => option.variantId === variantId) || null;
    }

    #getTotalCents() {
      const primaryVariant = this.#getPrimaryVariant();
      if (!primaryVariant) return null;

      let total = Number(primaryVariant.price) || 0;

      this.#getAddonLayers().forEach((layer) => {
        const option = this.#getSelectedAddonOption(layer);
        total += Number(option?.price) || 0;
      });

      return total;
    }

    #markPriceElementsIgnoredByTpo() {
      [
        this.priceRoot,
        this.salePriceEl,
        this.compareAtEl,
        this.saveBadgeEl,
        this.priceRoot?.querySelector('price-list')
      ].forEach((node) => {
        node?.classList?.add('tpo_ignore');
      });
    }

    #getCompareAtContribution(item) {
      if (!item) return 0;
      const price = Number(item.price) || 0;
      const compareAt = Number(item.compareAtPrice) || 0;
      return compareAt > price ? compareAt : price;
    }

    #getCompareTotalCents() {
      const primaryVariant = this.#getPrimaryVariant();
      if (!primaryVariant) return null;

      let total = this.#getCompareAtContribution(primaryVariant);

      this.#getAddonLayers().forEach((layer) => {
        const option = this.#getSelectedAddonOption(layer);
        total += this.#getCompareAtContribution(option);
      });

      return total;
    }

    #getSaveCents() {
      const saleCents = this.#getTotalCents();
      const compareCents = this.#getCompareTotalCents();
      if (saleCents == null || compareCents == null) return 0;
      return Math.max(0, compareCents - saleCents);
    }

    #updatePriceDisplay() {
      const saleCents = this.#getTotalCents();
      if (saleCents == null) return;

      const moneyFormat = this.data.moneyFormat || MONEY_FORMAT;
      const compareCents = this.#getCompareTotalCents() ?? saleCents;
      const saveCents = this.#getSaveCents();
      const onSale = saveCents > 0;

      if (this.priceEl) {
        this.priceEl.textContent = formatMoney(saleCents, moneyFormat);
      }

      if (this.salePriceEl) {
        this.salePriceEl.classList.toggle('text-on-sale', onSale);
      }

      if (this.compareAtEl && this.comparePriceEl) {
        if (onSale) {
          this.compareAtEl.hidden = false;
          this.comparePriceEl.textContent = formatMoney(compareCents, moneyFormat);
        } else {
          this.compareAtEl.hidden = true;
          this.comparePriceEl.textContent = '';
        }
      }

      if (this.saveBadgeEl) {
        if (onSale) {
          this.saveBadgeEl.hidden = false;
          this.saveBadgeEl.textContent = this.saveTemplate.replace(
            '__AMOUNT__',
            formatMoney(saveCents, moneyFormat)
          );
        } else {
          this.saveBadgeEl.hidden = true;
          this.saveBadgeEl.textContent = '';
        }
      }
    }

    #schedulePriceDisplayRefresh() {
      const run = () => this.#updatePriceDisplay();
      run();
      queueMicrotask(run);
      setTimeout(run, 0);
      setTimeout(run, 50);
    }

    #getTotalGrams() {
      const primaryVariant = this.#getPrimaryVariant();
      let total = Number(primaryVariant?.grams) || 0;

      this.#getAddonLayers().forEach((layer) => {
        const option = this.#getSelectedAddonOption(layer);
        total += Number(option?.grams) || 0;
      });

      return total;
    }

    #getLayerSlot(layerId) {
      if (!this.stageEl) return null;
      return this.stageEl.querySelector(`[data-layer-slot="${CSS.escape(String(layerId))}"]`);
    }

    async #setLayerImage(layerId, label, selected) {
      const slot = this.#getLayerSlot(layerId);
      if (!slot) return;

      const imgs = Array.from(slot.querySelectorAll('[data-layer-image]'));
      if (imgs.length === 0) return;

      let active = slot.querySelector('[data-layer-image].is-active') || imgs[0];
      let buffer = imgs.find((img) => img !== active) || imgs[1] || imgs[0];

      const requestId = (this.layerPending[layerId] || 0) + 1;
      this.layerPending[layerId] = requestId;

      if (!selected?.image) {
        slot.classList.add('is-empty');
        return;
      }

      const nextSrc = selected.image;
      const nextAlt = `${label}: ${selected.title}`;

      if (this.#urlsMatch(active.getAttribute('src'), nextSrc)) {
        active.alt = nextAlt;
        slot.classList.remove('is-empty');
        return;
      }

      await this.#preloadImage(nextSrc);
      if (this.layerPending[layerId] !== requestId) return;

      await this.#readyImageElement(buffer, nextSrc);
      if (this.layerPending[layerId] !== requestId) return;

      buffer.alt = nextAlt;
      active.alt = '';
      active.setAttribute('aria-hidden', 'true');
      buffer.removeAttribute('aria-hidden');

      buffer.classList.add('is-active');
      active.classList.remove('is-active');
      slot.classList.remove('is-empty');

      await this.#waitForPaint();
      if (this.layerPending[layerId] !== requestId) return;
    }

    #renderPrimaryOptions() {
      this.querySelectorAll('[data-config-primary-option]').forEach((button) => {
        const position = Number(button.getAttribute('data-option-position'));
        const value = button.getAttribute('data-option-value');
        const isSelected = this.primarySelected[position] === value;
        const isAvailable = this.#isPrimaryOptionAvailable(position, value);

        button.classList.toggle('is-selected', isSelected);
        button.toggleAttribute('disabled', !isAvailable);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
    }

    #renderAddonOptions() {
      this.#getAddonLayers().forEach((layer) => {
        const selected = this.#getSelectedAddonOption(layer);

        this.querySelectorAll(`[data-config-option][data-layer-id="${layer.id}"]`).forEach((button) => {
          const variantId = Number(button.getAttribute('data-variant-id'));
          button.classList.toggle('is-selected', variantId === selected?.variantId);
          button.setAttribute('aria-pressed', variantId === selected?.variantId ? 'true' : 'false');
        });

        if (!layer.affectsPreview) return;
        this.#setLayerImage(layer.id, layer.label, selected);
      });
    }

    #render() {
      this.#renderPrimaryOptions();

      const primary = this.#getPrimary();
      const primaryVariant = this.#getPrimaryVariant();
      if (primary?.affectsPreview) {
        this.#setLayerImage(primary.id, primary.label, primaryVariant);
      }

      this.#renderAddonOptions();

      this.#schedulePriceDisplayRefresh();

      this.#updateAtcState();
    }

    #updateAtcState() {
      if (!this.atcButton) return;

      const primaryVariant = this.#getPrimaryVariant();
      const primaryOk = Boolean(primaryVariant?.available);
      const addonsOk = this.#getAddonLayers().every((layer) => {
        const option = this.#getSelectedAddonOption(layer);
        return option && option.available;
      });

      this.atcButton.disabled = !(primaryOk && addonsOk);

      if (!primaryOk) {
        this.atcButton.textContent = this.atcButton.getAttribute('data-soldout-label') || 'Sold out';
      } else {
        this.atcButton.textContent = this.atcButton.getAttribute('data-atc-label') || 'Add to cart';
      }
    }

    #showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || 'Unable to add to cart.';
      this.errorEl.classList.add('is-visible');
    }

    #clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.classList.remove('is-visible');
    }

    async #addToCart() {
      this.#clearError();
      this.#updateAtcState();

      const primaryVariant = this.#getPrimaryVariant();

      if (this.atcButton?.disabled) {
        if (!primaryVariant?.available) {
          this.#showError(
            `“${this.data.productTitle}” / selected variant is sold out. Set inventory > 0 on that variant, or disable tracking / allow overselling.`
          );
        }
        return;
      }

      if (!primaryVariant) {
        this.#showError('Please select all product options.');
        return;
      }

      const configId = createConfigId();
      const properties = {
        _config_id: configId
      };

      (this.#getPrimary()?.optionGroups || []).forEach((group) => {
        properties[group.name] = this.primarySelected[group.position];
      });

      this.#getAddonLayers().forEach((layer) => {
        const option = this.#getSelectedAddonOption(layer);
        if (option) properties[layer.label] = option.title;
      });

      properties._config_grams = String(this.#getTotalGrams());

      const items = [
        {
          id: primaryVariant.variantId,
          quantity: 1,
          properties
        }
      ];

      this.#getAddonLayers().forEach((layer) => {
        const option = this.#getSelectedAddonOption(layer);
        if (!option) return;

        items.push({
          id: option.variantId,
          quantity: 1,
          properties: {
            _config_id: configId,
            _hidden: '1',
            _parent_title: this.data.productTitle,
            _layer: layer.label,
            _option: option.title
          }
        });
      });

      this.atcButton?.setAttribute('aria-busy', 'true');

      try {
        let sectionsToBundle = ['variant-added'];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname
          })
        });

        const responseJson = await response.json();
        if (!response.ok) {
          throw new Error(responseJson.description || responseJson.message || 'Add to cart failed');
        }

        if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        if (Array.isArray(cartContent.items)) {
          cartContent.item_count = cartContent.items.reduce((sum, item) => {
            const props = item.properties || {};
            const hidden =
              String(props._hidden || '') === '1' ||
              (props._layer != null && props._parent_title != null);
            return hidden ? sum : sum + (Number(item.quantity) || 0);
          }, 0);
        }

        this.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.items || [responseJson],
              cart: cartContent
            }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cartContent
            }
          })
        );
      } catch (error) {
        let message = error.message || 'Add to cart failed';
        if (/sold out/i.test(message)) {
          message +=
            ' Please check inventory on the selected primary variant and every selected addon variant (including defaults).';
        }
        this.#showError(message);
      } finally {
        this.atcButton?.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('keyboard-configurator')) {
    customElements.define('keyboard-configurator', KeyboardConfigurator);
  }
})();
